// src/components/auth/AvatarUpload.tsx
// Drag-and-drop + click-to-upload avatar selector used inside ProfileModal.
// Does NOT upload to Firebase — it only selects the file and previews it.
// The actual upload happens in authService.handleProfileUpdate().

import React, { useCallback, useRef, useState } from 'react';

const C = {
  accent:  '#4f8cf7',
  muted:   '#8b9cc4',
  error:   '#f87171',
  success: '#34d399',
};

export interface AvatarUploadProps {
  currentUrl: string | null;
  initial: string;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  hasAvatar: boolean;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const MAX_BYTES = 2 * 1024 * 1024;

export function AvatarUpload({
  currentUrl,
  initial,
  onFileSelect,
  onRemove,
  hasAvatar,
}: AvatarUploadProps): React.ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview,    setPreview]    = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [feedback,   setFeedback]   = useState<{ msg: string; ok: boolean } | null>(null);

  const handleFile = useCallback(
    (file: File | null | undefined) => {
      if (!file) return;
      if (!ALLOWED_TYPES.includes(file.type)) {
        setFeedback({ msg: 'Please select a JPG, PNG, or GIF image.', ok: false });
        return;
      }
      if (file.size > MAX_BYTES) {
        setFeedback({ msg: 'File must be smaller than 2 MB.', ok: false });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
        setFeedback({ msg: 'Image ready. Click "Save Changes" to upload.', ok: true });
      };
      reader.readAsDataURL(file);
      onFileSelect(file);
    },
    [onFileSelect]
  );

  const onDragEnter = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const onDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(false); };
  const onDragOver  = (e: React.DragEvent) => { e.preventDefault(); };
  const onDrop      = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) handleFile(file);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0]);
  };

  const handleRemove = () => {
    setPreview(null);
    setFeedback({ msg: 'Avatar removed. Click "Save Changes" to update.', ok: true });
    if (fileInputRef.current) fileInputRef.current.value = '';
    onRemove();
  };

  const displayUrl = preview || currentUrl;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      {/* Avatar preview */}
      <div style={{
        width: '96px', height: '96px', borderRadius: '50%',
        border: `3px solid ${C.accent}`,
        background: 'rgba(79,140,247,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '32px', fontWeight: 700, color: C.accent,
        overflow: 'hidden', flexShrink: 0,
        boxShadow: '0 0 0 4px rgba(79,140,247,0.12)',
      }}>
        {displayUrl
          ? <img src={displayUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span>{initial}</span>
        }
      </div>

      {/* Drop zone */}
      <div
        style={{
          width: '100%', padding: '20px',
          border: `2px dashed ${isDragOver ? C.accent : 'rgba(255,255,255,0.1)'}`,
          borderRadius: '12px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
          cursor: 'pointer',
          transition: 'border-color 0.2s, background 0.2s',
          background: isDragOver ? 'rgba(79,140,247,0.05)' : 'transparent',
          textAlign: 'center',
        }}
        onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button" tabIndex={0} aria-label="Upload avatar image"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
      >
        {/* Camera icon */}
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
        <p style={{ color: C.muted, fontSize: '0.82rem', margin: 0 }}>
          <span style={{ color: C.accent, fontWeight: 600 }}>Click to upload</span> or drag &amp; drop
        </p>
        <p style={{ color: C.muted, fontSize: '0.78rem', margin: 0 }}>JPG, PNG, GIF — max 2 MB</p>
      </div>

      <input
        ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif"
        style={{ display: 'none' }} onChange={onInputChange} aria-hidden="true"
      />

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          style={{
            padding: '8px 18px', background: C.accent, border: 'none',
            borderRadius: '999px', color: '#fff', fontSize: '0.82rem',
            fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          Choose Image
        </button>
        {(hasAvatar || preview) && (
          <button
            type="button"
            style={{
              padding: '8px 18px', background: 'transparent',
              border: `1px solid rgba(248,113,113,0.4)`,
              borderRadius: '999px', color: C.error, fontSize: '0.82rem',
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
            onClick={handleRemove}
          >
            Remove
          </button>
        )}
      </div>

      {feedback && (
        <p style={{ fontSize: '0.78rem', color: feedback.ok ? C.success : C.error, textAlign: 'center', margin: 0 }}
          role={feedback.ok ? undefined : 'alert'}
        >
          {feedback.msg}
        </p>
      )}
    </div>
  );
}
